/**
 * LogoIconCompact - Optimized for small sizes (favicon, header)
 * Optimized for maximum visibility at 16x16 and 32x32 pixel sizes
 */

import React from 'react';

interface LogoIconCompactProps {
  size?: 'small' | 'medium' | 'large' | 'hero' | number;
  className?: string;
}

const compactSizeMap = {
  small: 24,
  medium: 32,
  large: 48,
  hero: 192
};

export const LogoIconCompact: React.FC<LogoIconCompactProps> = ({ size = 'medium', className = '' }) => {
  const pixelSize = typeof size === 'number' ? size : compactSizeMap[size];
  return (
    <svg 
      width={pixelSize} 
      height={pixelSize} 
      viewBox="0 0 256 256" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Code League logo"
    >
      <defs>
        <linearGradient id={`bg-compact-${pixelSize}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#020617"/>
          <stop offset="40%" stopColor="#050B24"/>
          <stop offset="100%" stopColor="#0B1029"/>
        </linearGradient>

        <linearGradient id={`border-compact-${pixelSize}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2563EB"/>
          <stop offset="50%" stopColor="#D946EF"/>
          <stop offset="100%" stopColor="#FF8A24"/>
        </linearGradient>

        <linearGradient id={`gold-compact-${pixelSize}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FACC15"/>
          <stop offset="50%" stopColor="#FFB74A"/>
          <stop offset="100%" stopColor="#FF8A24"/>
        </linearGradient>
      </defs>

      {/* Outer badge */}
      <rect x="8" y="8" width="240" height="240" rx="48" ry="48"
            fill={`url(#bg-compact-${pixelSize})`}
            stroke={`url(#border-compact-${pixelSize})`} strokeWidth="4"/>

      {/* Dark inner field */}
      <circle cx="128" cy="128" r="95"
              fill="#050B24"/>

      {/* Centered symbol group */}
      <g transform="translate(128,6)">
        {/* Curly braces { } - larger and bolder for small sizes */}
        <text x="-64" y="156"
              fontSize="120"
              textAnchor="middle"
              fontFamily="monospace"
              fontWeight="bold"
              fill={`url(#gold-compact-${pixelSize})`}>{'{'}</text>

        <text x="64" y="156"
              fontSize="120"
              textAnchor="middle"
              fontFamily="monospace"
              fontWeight="bold"
              fill={`url(#gold-compact-${pixelSize})`}>{'}'}</text>

        {/* Center glyph ">" - bolder */}
        <text x="0" y="136"
              fontSize="64"
              textAnchor="middle"
              fontFamily="monospace"
              fontWeight="bold"
              fill="#FF8A24">&gt;</text>

        {/* Underscore - bolder */}
        <text x="0" y="148"
              fontSize="64"
              textAnchor="middle"
              fontFamily="monospace"
              fontWeight="bold"
              fill="#00E5FF">_</text>
      </g>
    </svg>
  );
};

